import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { formatDate } from '../lib/api';
import { useAuth } from '../lib/auth';
import { StatusBadge, SourceBadge } from '../components/StatusBadge';
import { SLAWidget } from '../components/SLAWidget';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  EnhancedTable,
  StickyTableHeader,
  SortableHeader,
  TableHeader,
  EnhancedTableBody,
  EnhancedTableRow,
  EnhancedTableCell,
  TableEmptyState,
  SelectAllCell,
  SelectRowCell,
  SavedFiltersDropdown,
  FilterChips,
  BulkActionBar,
  TableSettingsDropdown,
  EnhancedEmptyState,
} from '../components/ui/enhanced-table';
import { useTableSort, useSavedFilters, useTableSelection, useTableSettings } from '../hooks/useTableEnhancements';
import { 
  ClipboardList, 
  Calendar, 
  Search, 
  X, 
  Download,
  AlertTriangle,
  Clock,
  Wrench,
  Package,
  Truck,
  User,
  UserPlus,
  ArrowRight,
  ArrowLeft,
  Shield,
  LayoutDashboard,
} from 'lucide-react';
import { toast } from 'sonner';

const WO_STATUSES = ['OPEN', 'ASSIGNED', 'IN_PROGRESS', 'WAITING_PARTS', 'WAITING_VENDOR', 'COMPLETED', 'CLOSED'];

const TABS = [
  { key: 'all', filter: {} },
  { key: 'myOrders', filter: { my_orders: true } },
  { key: 'overdue', filter: { overdue: true } },
  { key: 'slaBreached', filter: { sla_breached: true } },
  { key: 'slaNearBreach', filter: { sla_near_breach: true } },
  { key: 'waitingParts', filter: { status: 'WAITING_PARTS' } },
  { key: 'waitingVendor', filter: { status: 'WAITING_VENDOR' } },
  { key: 'preventive', filter: { type: 'preventive' } },
];

// Safe transitions allowed for bulk status updates
const BULK_STATUS_TRANSITIONS = {
  'ASSIGNED': ['IN_PROGRESS'],
  'IN_PROGRESS': ['WAITING_PARTS', 'WAITING_VENDOR'],
  'WAITING_PARTS': ['IN_PROGRESS'],
  'WAITING_VENDOR': ['IN_PROGRESS'],
};

export default function WorkOrdersPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [orders, setOrders] = useState([]);
  const [branches, setBranches] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);

  // Tab state
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'all');

  // Filters
  const [fBranch, setFBranch] = useState('all');
  const [fStatus, setFStatus] = useState('all');
  const [fType, setFType] = useState('all');
  const [fSource, setFSource] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(true); // Default TRUE for list

  // Sorting with persistence
  const { sortField, sortDir, toggleSort, sortData } = useTableSort('wo_page', 'created_at', 'desc');

  // Saved filters
  const { savedFilters, saveCurrentFilters, saveFilterPreset, deleteFilterPreset, loadCurrentFilters } = useSavedFilters('wo_page');

  // Column visibility and density settings
  const woColumns = [
    { key: 'id', label: 'ID' },
    { key: 'type', label: t('common.type') },
    { key: 'source', label: t('source.label') },
    { key: 'status', label: t('common.status') },
    { key: 'sla', label: 'SLA' },
    { key: 'branch', label: t('common.branch') },
    { key: 'room', label: t('common.room') },
    { key: 'assignedTo', label: t('workOrder.assignedTo') },
    { key: 'dueDate', label: t('workOrder.dueDate') },
    { key: 'duration', label: t('workOrder.duration', 'Duration') },
    { key: 'createdAt', label: t('common.createdAt') },
  ];
  const lockedColumns = ['id', 'status', 'branch', 'room', 'duration']; // Cannot be hidden
  const tableSettings = useTableSettings('wo_page', woColumns, lockedColumns);

  // Bulk action dialogs
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkTechnicianId, setBulkTechnicianId] = useState('');
  const [bulkNewStatus, setBulkNewStatus] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Load saved filters on mount
  useEffect(() => {
    const saved = loadCurrentFilters();
    if (saved) {
      if (saved.branch) setFBranch(saved.branch);
      if (saved.status) setFStatus(saved.status);
      if (saved.type) setFType(saved.type);
      if (saved.tab) setActiveTab(saved.tab);
    }
  }, []);

  // Save current filters when they change
  useEffect(() => {
    saveCurrentFilters({ branch: fBranch, status: fStatus, type: fType, source: fSource, tab: activeTab });
  }, [fBranch, fStatus, fType, fSource, activeTab, saveCurrentFilters]);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => { 
    loadOrders(); 
  }, [fBranch, fSource, activeTab, showArchived]);

  const loadInitialData = async () => {
    try {
      const [branchRes, userRes] = await Promise.all([
        api.get('/branches'),
        api.get('/users'),
      ]);
      setBranches(branchRes.data);
      setTechnicians(userRes.data.filter(u => u.role === 'technician'));
    } catch (err) {
      toast.error(t('toast.error'));
    }
  };

  const loadOrders = async () => {
    try {
      const params = { include_archived: showArchived };
      if (fBranch !== 'all') params.branch_id = fBranch;
      if (fSource !== 'all') params.maintenance_source = fSource;
      
      const tabConfig = TABS.find(t => t.key === activeTab);
      if (tabConfig?.filter) {
        if (tabConfig.filter.my_orders && user?.role === 'technician') {
          params.my_orders = true;
        }
        if (tabConfig.filter.status) {
          params.status = tabConfig.filter.status;
        }
      }
      
      const { data } = await api.get('/work-orders', { params });
      // Handle new response format with data/meta
      setOrders(data.data || data);
    } catch (err) { 
      toast.error(t('toast.error')); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFStatus('all');
    setFType('all');
    selection.clearSelection();
    setSearchParams({ tab });
  };

  const clearFilters = () => {
    setFBranch('all');
    setFStatus('all');
    setFType('all');
    setFSource('all');
    setSearchQuery('');
  };

  const applyFilterPreset = (filters) => {
    if (filters.branch) setFBranch(filters.branch);
    if (filters.status) setFStatus(filters.status);
    if (filters.type) setFType(filters.type);
    if (filters.source) setFSource(filters.source);
    if (filters.tab) setActiveTab(filters.tab);
    toast.success(t('filters.applied'));
  };

  // Filter and sort data locally
  const filteredOrders = useMemo(() => {
    let result = [...orders];
    
    if (fStatus !== 'all') {
      result = result.filter(wo => wo.status === fStatus);
    }
    
    if (fType !== 'all') {
      result = result.filter(wo => wo.type === fType);
    }
    
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(wo => 
        wo.id?.toLowerCase().includes(q) || 
        wo.branch_name?.toLowerCase().includes(q) ||
        wo.assigned_to_name?.toLowerCase().includes(q)
      );
    }
    
    if (activeTab === 'overdue') {
      const now = new Date().toISOString();
      result = result.filter(wo => 
        wo.target_due_date && 
        wo.target_due_date < now && 
        !['CLOSED', 'COMPLETED'].includes(wo.status)
      );
    }
    if (activeTab === 'preventive') {
      result = result.filter(wo => wo.type === 'preventive');
    }
    if (activeTab === 'myOrders' && user?.role === 'technician') {
      result = result.filter(wo => wo.assigned_to === user.id);
    }
    if (activeTab === 'slaBreached') {
      result = result.filter(wo => 
        wo.sla?.is_response_breached || wo.sla?.is_resolution_breached
      );
    }
    if (activeTab === 'slaNearBreach') {
      result = result.filter(wo => 
        (wo.sla?.is_response_near_breach || wo.sla?.is_resolution_near_breach) &&
        !wo.sla?.is_response_breached && !wo.sla?.is_resolution_breached
      );
    }
    
    return sortData(result);
  }, [orders, fStatus, fType, searchQuery, activeTab, user, sortData]);

  // Selection
  const selection = useTableSelection(filteredOrders);

  // Tab counts
  const tabCounts = useMemo(() => {
    const now = new Date().toISOString();
    const counts = {};
    counts.all = orders.length;
    counts.myOrders = orders.filter(wo => wo.assigned_to === user?.id).length;
    counts.overdue = orders.filter(wo => 
      wo.target_due_date && 
      wo.target_due_date < now && 
      !['CLOSED', 'COMPLETED'].includes(wo.status)
    ).length;
    counts.slaBreached = orders.filter(wo => 
      wo.sla?.is_response_breached || wo.sla?.is_resolution_breached
    ).length;
    counts.slaNearBreach = orders.filter(wo => 
      (wo.sla?.is_response_near_breach || wo.sla?.is_resolution_near_breach) &&
      !wo.sla?.is_response_breached && !wo.sla?.is_resolution_breached
    ).length;
    counts.waitingParts = orders.filter(wo => wo.status === 'WAITING_PARTS').length;
    counts.waitingVendor = orders.filter(wo => wo.status === 'WAITING_VENDOR').length;
    counts.preventive = orders.filter(wo => wo.type === 'preventive').length;
    return counts;
  }, [orders, user]);

  // Quick filter chips configuration
  const filterChips = useMemo(() => [
    {
      key: 'overdue',
      label: t('chips.overdue'),
      icon: AlertTriangle,
      count: tabCounts.overdue,
      countVariant: 'warning',
      isActive: (filters) => activeTab === 'overdue',
      onActivate: () => handleTabChange('overdue'),
      onDeactivate: () => handleTabChange('all'),
    },
    {
      key: 'slaBreached',
      label: t('sla.slaBreached'),
      icon: Shield,
      count: tabCounts.slaBreached,
      countVariant: 'destructive',
      isActive: () => activeTab === 'slaBreached',
      onActivate: () => handleTabChange('slaBreached'),
      onDeactivate: () => handleTabChange('all'),
    },
    {
      key: 'slaNearBreach',
      label: t('sla.slaNearBreach'),
      icon: Clock,
      count: tabCounts.slaNearBreach,
      countVariant: 'warning',
      isActive: () => activeTab === 'slaNearBreach',
      onActivate: () => handleTabChange('slaNearBreach'),
      onDeactivate: () => handleTabChange('all'),
    },
    {
      key: 'waitingParts',
      label: t('chips.waitingParts'),
      icon: Package,
      count: tabCounts.waitingParts,
      isActive: () => fStatus === 'WAITING_PARTS' || activeTab === 'waitingParts',
      onActivate: () => handleTabChange('waitingParts'),
      onDeactivate: () => handleTabChange('all'),
    },
    {
      key: 'waitingVendor',
      label: t('chips.waitingVendor'),
      icon: Truck,
      count: tabCounts.waitingVendor,
      isActive: () => fStatus === 'WAITING_VENDOR' || activeTab === 'waitingVendor',
      onActivate: () => handleTabChange('waitingVendor'),
      onDeactivate: () => handleTabChange('all'),
    },
    {
      key: 'preventive',
      label: t('chips.preventive'),
      icon: Calendar,
      count: tabCounts.preventive,
      isActive: () => fType === 'preventive' || activeTab === 'preventive',
      onActivate: () => handleTabChange('preventive'),
      onDeactivate: () => handleTabChange('all'),
    },
    {
      key: 'myOrders',
      label: t('chips.myOrders'),
      icon: User,
      count: tabCounts.myOrders,
      isActive: () => activeTab === 'myOrders',
      onActivate: () => handleTabChange('myOrders'),
      onDeactivate: () => handleTabChange('all'),
    },
  ], [t, tabCounts, activeTab, fStatus, fType]);

  const activeChips = filterChips.filter(chip => chip.isActive());

  const exportCSV = () => {
    const dataToExport = selection.selectedCount > 0 
      ? selection.selectedItems
      : filteredOrders;
    
    const csvCell = (value) => {
      const s = value === null || value === undefined ? '' : String(value);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const headers = ['ID', 'Type', 'Source', 'Status', 'Branch', 'Room', 'Assigned To', 'Due Date', 'Duration (min)', 'Created At'];
    const rows = dataToExport.map(wo => [
      wo.id,
      wo.type,
      wo.maintenance_source || 'UNKNOWN',
      wo.status,
      wo.branch_name,
      wo.room_name || 'No room assigned',
      wo.assigned_to_name || '',
      wo.target_due_date || '',
      wo.duration_minutes ?? '',
      wo.created_at,
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.map(csvCell).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `work_orders_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success(t('common.exportCSV'));
  };

  // Bulk assign handler
  const handleBulkAssign = async () => {
    if (!bulkTechnicianId) {
      toast.error(t('bulk.selectTechnician'));
      return;
    }
    
    setBulkProcessing(true);
    try {
      const { data } = await api.post('/work-orders/bulk/assign', {
        wo_ids: Array.from(selection.selectedIds),
        technician_id: bulkTechnicianId,
      });
      
      if (data.success_count > 0) {
        toast.success(t('bulk.bulkSuccess', { success: data.success_count, total: data.total }));
      }
      if (data.failed_count > 0) {
        toast.error(t('bulk.bulkFailed', { failed: data.failed_count }));
      }
      
      setBulkAssignOpen(false);
      setBulkTechnicianId('');
      selection.clearSelection();
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.error'));
    } finally {
      setBulkProcessing(false);
    }
  };

  // Bulk status update handler
  const handleBulkStatusUpdate = async () => {
    if (!bulkNewStatus) {
      toast.error(t('bulk.selectStatus'));
      return;
    }
    
    setBulkProcessing(true);
    try {
      const { data } = await api.post('/work-orders/bulk/status', {
        wo_ids: Array.from(selection.selectedIds),
        new_status: bulkNewStatus,
      });
      
      if (data.success_count > 0) {
        toast.success(t('bulk.bulkSuccess', { success: data.success_count, total: data.total }));
      }
      if (data.failed_count > 0) {
        toast.error(t('bulk.bulkFailed', { failed: data.failed_count }));
      }
      
      setBulkStatusOpen(false);
      setBulkNewStatus('');
      selection.clearSelection();
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.error'));
    } finally {
      setBulkProcessing(false);
    }
  };

  // Calculate eligible counts for bulk actions
  const eligibleForAssign = useMemo(() => 
    selection.selectedItems.filter(wo => wo.status === 'OPEN').length,
    [selection.selectedItems]
  );

  const eligibleStatusTransitions = useMemo(() => {
    const transitions = {};
    selection.selectedItems.forEach(wo => {
      const valid = BULK_STATUS_TRANSITIONS[wo.status] || [];
      valid.forEach(s => {
        transitions[s] = (transitions[s] || 0) + 1;
      });
    });
    return transitions;
  }, [selection.selectedItems]);

  const hasActiveFilters = fBranch !== 'all' || fStatus !== 'all' || fType !== 'all' || fSource !== 'all' || searchQuery;
  const canBulkAction = ['admin', 'supervisor'].includes(user?.role);
  const isManager = ['admin', 'supervisor', 'super_admin'].includes(user?.role);

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t('common.loading')}</div>;

  return (
    <div data-testid="work-orders-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          {/* Back to Board button - managers only */}
          {isManager && (
            <Button
              variant="ghost"
              size="sm"
              className="h-9 px-3"
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-board-btn"
            >
              <ArrowLeft className="w-4 h-4 me-1.5" />
              {t('nav.backToBoard')}
            </Button>
          )}
          <div>
            <h1 className="font-barlow font-bold text-2xl text-foreground tracking-tight">{t('workOrder.title')}</h1>
            <p className="text-muted-foreground text-sm mt-1">{filteredOrders.length} {t('workOrder.title').toLowerCase()}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <TableSettingsDropdown
            columns={woColumns}
            lockedColumns={lockedColumns}
            density={tableSettings.density}
            onToggleColumn={tableSettings.toggleColumn}
            onSetDensity={tableSettings.setDensity}
            onResetAll={tableSettings.resetAll}
            isVisible={tableSettings.isVisible}
            isLocked={tableSettings.isLocked}
            t={t}
          />
          <SavedFiltersDropdown
            savedFilters={savedFilters}
            onSave={saveFilterPreset}
            onApply={applyFilterPreset}
            onDelete={deleteFilterPreset}
            currentFilters={{ branch: fBranch, status: fStatus, type: fType, tab: activeTab }}
            t={t}
          />
          <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-csv-btn">
            <Download className="w-4 h-4 me-2" /> {t('common.exportCSV')}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="bg-muted/50 p-1 w-full sm:w-auto flex overflow-x-auto gap-1 no-scrollbar">
          {TABS.map(tab => (
            <TabsTrigger 
              key={tab.key} 
              value={tab.key}
              className={`data-[state=active]:bg-card data-[state=active]:shadow-sm ${
                tab.key === 'overdue' && tabCounts.overdue > 0 
                  ? 'data-[state=active]:text-rose-500' 
                  : 'data-[state=active]:text-brand'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              {t(`workOrder.tab.${tab.key}`, tab.key === 'all' ? t('common.all') : tab.key)}
              <Badge 
                variant="secondary" 
                className={`ms-2 text-xs ${
                  tab.key === 'overdue' && tabCounts.overdue > 0 ? 'bg-rose-100 text-rose-700' : ''
                }`}
              >
                {tabCounts[tab.key] || 0}
              </Badge>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Quick Filter Chips */}
      <FilterChips
        chips={filterChips}
        activeChips={activeChips}
        onToggle={(chip) => chip.isActive() ? chip.onDeactivate() : chip.onActivate()}
      />

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('common.search')}
            className="ps-9"
            data-testid="wo-search"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <Select value={fBranch} onValueChange={setFBranch}>
          <SelectTrigger className="w-full sm:w-[160px]" data-testid="wo-filter-branch"><SelectValue placeholder={t('common.branch')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')} {t('common.branches')}</SelectItem>
            {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>

        {!['waitingParts', 'waitingVendor'].includes(activeTab) && (
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger className="w-full sm:w-[150px]" data-testid="wo-filter-status"><SelectValue placeholder={t('common.status')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all')}</SelectItem>
              {WO_STATUSES.map(s => <SelectItem key={s} value={s}>{t(`status.${s}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        {activeTab !== 'preventive' && (
          <Select value={fType} onValueChange={setFType}>
            <SelectTrigger className="w-full sm:w-[130px]" data-testid="wo-filter-type"><SelectValue placeholder={t('common.type')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('workOrder.allTypes')}</SelectItem>
              <SelectItem value="corrective">{t('workOrder.corrective')}</SelectItem>
              <SelectItem value="preventive">{t('workOrder.preventive')}</SelectItem>
            </SelectContent>
          </Select>
        )}

        <Select value={fSource} onValueChange={setFSource}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="wo-filter-source"><SelectValue placeholder={t('source.label')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('source.all')}</SelectItem>
            <SelectItem value="GAME_MASTER_REPORT">{t('source.GAME_MASTER_REPORT')}</SelectItem>
            <SelectItem value="TECHNICIAN_DISCOVERY">{t('source.TECHNICIAN_DISCOVERY')}</SelectItem>
            <SelectItem value="PM_GENERATED">{t('source.PM_GENERATED')}</SelectItem>
            <SelectItem value="MANAGER_CREATED">{t('source.MANAGER_CREATED')}</SelectItem>
            <SelectItem value="UNKNOWN">{t('source.UNKNOWN')}</SelectItem>
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
            <X className="w-4 h-4 me-1" /> {t('filters.resetFilters')}
          </Button>
        )}

        {/* Show archived toggle */}
        <div className="flex items-center gap-2 ms-auto">
          <Checkbox 
            id="show-archived" 
            checked={showArchived} 
            onCheckedChange={setShowArchived}
            data-testid="show-archived-checkbox"
          />
          <Label htmlFor="show-archived" className="text-sm text-muted-foreground cursor-pointer">
            {t('workOrder.showArchived', 'Show archived')}
          </Label>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {canBulkAction && selection.selectedCount > 0 && (
        <BulkActionBar selectedCount={selection.selectedCount} onClear={selection.clearSelection}>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => setBulkAssignOpen(true)}
            disabled={eligibleForAssign === 0}
            data-testid="bulk-assign-btn"
          >
            <UserPlus className="w-4 h-4 me-2" />
            {t('bulk.bulkAssign')}
            {eligibleForAssign > 0 && <Badge variant="secondary" className="ms-2">{eligibleForAssign}</Badge>}
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => setBulkStatusOpen(true)}
            disabled={Object.keys(eligibleStatusTransitions).length === 0}
            data-testid="bulk-status-btn"
          >
            <ArrowRight className="w-4 h-4 me-2" />
            {t('bulk.bulkStatusUpdate')}
          </Button>
        </BulkActionBar>
      )}

      {/* Table */}
      <Card className="bg-card border-border shadow-sm">
        <CardContent className="p-0">
          <EnhancedTable stickyHeader>
            <StickyTableHeader>
              <tr className="border-b border-border">
                {canBulkAction && (
                  <SelectAllCell
                    isAllSelected={selection.isAllSelected}
                    isSomeSelected={selection.isSomeSelected}
                    onToggle={selection.toggleSelectAll}
                  />
                )}
                {tableSettings.isVisible('id') && (
                  <SortableHeader field="id" currentField={sortField} currentDir={sortDir} onSort={toggleSort} className={tableSettings.densityClasses.header}>
                    ID
                  </SortableHeader>
                )}
                {tableSettings.isVisible('type') && (
                  <TableHeader className={tableSettings.densityClasses.header}>{t('common.type')}</TableHeader>
                )}
                {tableSettings.isVisible('source') && (
                  <TableHeader className={tableSettings.densityClasses.header}>{t('source.label')}</TableHeader>
                )}
                {tableSettings.isVisible('status') && (
                  <TableHeader className={tableSettings.densityClasses.header}>{t('common.status')}</TableHeader>
                )}
                {tableSettings.isVisible('sla') && (
                  <TableHeader className={tableSettings.densityClasses.header}>SLA</TableHeader>
                )}
                {tableSettings.isVisible('branch') && (
                  <TableHeader className={tableSettings.densityClasses.header}>{t('common.branch')}</TableHeader>
                )}
                {tableSettings.isVisible('room') && (
                  <TableHeader className={tableSettings.densityClasses.header}>{t('common.room')}</TableHeader>
                )}
                {tableSettings.isVisible('assignedTo') && (
                  <TableHeader className={tableSettings.densityClasses.header}>{t('workOrder.assignedTo')}</TableHeader>
                )}
                {tableSettings.isVisible('dueDate') && (
                  <SortableHeader field="target_due_date" currentField={sortField} currentDir={sortDir} onSort={toggleSort} className={tableSettings.densityClasses.header}>
                    {t('workOrder.dueDate')}
                  </SortableHeader>
                )}
                {tableSettings.isVisible('duration') && (
                  <SortableHeader field="duration_minutes" currentField={sortField} currentDir={sortDir} onSort={toggleSort} className={tableSettings.densityClasses.header}>
                    {t('workOrder.duration', 'Duration')}
                  </SortableHeader>
                )}
                {tableSettings.isVisible('createdAt') && (
                  <SortableHeader field="created_at" currentField={sortField} currentDir={sortDir} onSort={toggleSort} className={tableSettings.densityClasses.header}>
                    {t('common.createdAt')}
                  </SortableHeader>
                )}
              </tr>
            </StickyTableHeader>
            <EnhancedTableBody>
              {filteredOrders.length === 0 && (
                <EnhancedEmptyState
                  icon={ClipboardList}
                  title={hasActiveFilters ? t('emptyState.noResults') : t('workOrder.noOrders')}
                  description={hasActiveFilters ? t('emptyState.noResultsDesc') : undefined}
                  colSpan={canBulkAction ? tableSettings.visibleColumns.length + 1 : tableSettings.visibleColumns.length}
                  hasFilters={hasActiveFilters}
                  onResetFilters={clearFilters}
                  t={t}
                />
              )}
              {filteredOrders.map(wo => {
                const isOverdue = wo.target_due_date && 
                  wo.target_due_date < new Date().toISOString() && 
                  !['CLOSED', 'COMPLETED'].includes(wo.status);
                
                return (
                  <EnhancedTableRow
                    key={wo.id}
                    isSelected={selection.isSelected(wo.id)}
                    isHighlighted={isOverdue}
                    className="cursor-pointer"
                    onClick={() => navigate(`/work-orders/${wo.id}`)}
                    data-testid={`wo-row-${wo.id}`}
                  >
                    {canBulkAction && (
                      <SelectRowCell
                        isSelected={selection.isSelected(wo.id)}
                        onToggle={() => selection.toggleSelect(wo.id)}
                      />
                    )}
                    {tableSettings.isVisible('id') && (
                      <EnhancedTableCell className={`font-mono text-xs text-muted-foreground ${tableSettings.densityClasses.cell}`}>{wo.id.slice(0, 8)}</EnhancedTableCell>
                    )}
                    {tableSettings.isVisible('type') && (
                      <EnhancedTableCell className={tableSettings.densityClasses.cell}>
                        <Badge variant={wo.type === 'preventive' ? 'default' : 'outline'} 
                               className={wo.type === 'preventive' ? 'bg-blue-100 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400' : ''}>
                          {wo.type === 'preventive' && <Calendar className="w-3 h-3 me-1" />}
                          {t(`workOrder.${wo.type}`)}
                        </Badge>
                      </EnhancedTableCell>
                    )}
                    {tableSettings.isVisible('source') && (
                      <EnhancedTableCell className={tableSettings.densityClasses.cell}>
                        <SourceBadge source={wo.maintenance_source} compact />
                      </EnhancedTableCell>
                    )}
                    {tableSettings.isVisible('status') && (
                      <EnhancedTableCell className={tableSettings.densityClasses.cell}><StatusBadge status={wo.status} /></EnhancedTableCell>
                    )}
                    {tableSettings.isVisible('sla') && (
                      <EnhancedTableCell className={tableSettings.densityClasses.cell}>
                        <SLAWidget sla={wo.sla} compact />
                      </EnhancedTableCell>
                    )}
                    {tableSettings.isVisible('branch') && (
                      <EnhancedTableCell className={`text-foreground ${tableSettings.densityClasses.cell}`}>{wo.branch_name}</EnhancedTableCell>
                    )}
                    {tableSettings.isVisible('room') && (
                      <EnhancedTableCell className={`text-foreground whitespace-nowrap ${tableSettings.densityClasses.cell}`} data-testid={`wo-room-${wo.id}`}>
                        {wo.room_name || <span className="text-muted-foreground italic">{t('common.noRoom')}</span>}
                      </EnhancedTableCell>
                    )}
                    {tableSettings.isVisible('assignedTo') && (
                      <EnhancedTableCell className={`text-muted-foreground ${tableSettings.densityClasses.cell}`}>{wo.assigned_to_name || t('workOrder.unassigned')}</EnhancedTableCell>
                    )}
                    {tableSettings.isVisible('dueDate') && (
                      <EnhancedTableCell className={`text-xs ${isOverdue ? 'text-rose-600 font-medium' : 'text-muted-foreground'} ${tableSettings.densityClasses.cell}`}>
                        {formatDate(wo.target_due_date)}
                      </EnhancedTableCell>
                    )}
                    {tableSettings.isVisible('duration') && (
                      <EnhancedTableCell className={`text-xs text-muted-foreground ${tableSettings.densityClasses.cell}`} data-testid={`wo-duration-${wo.id}`}>
                        {wo.duration_minutes !== null && wo.duration_minutes !== undefined ? `${wo.duration_minutes} min` : '—'}
                      </EnhancedTableCell>
                    )}
                    {tableSettings.isVisible('createdAt') && (
                      <EnhancedTableCell className={`text-xs text-muted-foreground ${tableSettings.densityClasses.cell}`}>
                        {formatDate(wo.created_at)}
                      </EnhancedTableCell>
                    )}
                  </EnhancedTableRow>
                );
              })}
            </EnhancedTableBody>
          </EnhancedTable>
        </CardContent>
      </Card>

      {/* Bulk Assign Dialog */}
      <Dialog open={bulkAssignOpen} onOpenChange={setBulkAssignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('bulk.bulkAssign')}</DialogTitle>
            <DialogDescription>
              {t('bulk.bulkActionWarning', { count: selection.selectedCount })}
              {eligibleForAssign < selection.selectedCount && (
                <span className="block mt-2 text-amber-600">
                  {t('bulk.eligibleCount', { count: eligibleForAssign })} ({t('bulk.wrongStatus')}: {selection.selectedCount - eligibleForAssign})
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('bulk.assignTo')}</Label>
              <Select value={bulkTechnicianId} onValueChange={setBulkTechnicianId}>
                <SelectTrigger data-testid="bulk-technician-select">
                  <SelectValue placeholder={t('bulk.selectTechnician')} />
                </SelectTrigger>
                <SelectContent>
                  {technicians.map(tech => (
                    <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAssignOpen(false)}>{t('common.cancel')}</Button>
            <Button 
              onClick={handleBulkAssign} 
              disabled={bulkProcessing || !bulkTechnicianId || eligibleForAssign === 0}
              className="bg-brand hover:bg-brand-hover"
              data-testid="bulk-assign-confirm-btn"
            >
              {bulkProcessing ? t('bulk.processing') : t('bulk.bulkAssign')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Status Update Dialog */}
      <Dialog open={bulkStatusOpen} onOpenChange={setBulkStatusOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('bulk.bulkStatusUpdate')}</DialogTitle>
            <DialogDescription>
              {t('bulk.bulkActionWarning', { count: selection.selectedCount })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('bulk.selectStatus')}</Label>
              <Select value={bulkNewStatus} onValueChange={setBulkNewStatus}>
                <SelectTrigger data-testid="bulk-status-select">
                  <SelectValue placeholder={t('bulk.selectStatus')} />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(eligibleStatusTransitions).map(([status, count]) => (
                    <SelectItem key={status} value={status}>
                      {t(`status.${status}`)} ({count} {t('bulk.eligibleCount', { count }).split(' ').slice(-1)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {Object.keys(eligibleStatusTransitions).length === 0 && (
                <p className="text-sm text-muted-foreground">{t('bulk.noEligibleItems')}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkStatusOpen(false)}>{t('common.cancel')}</Button>
            <Button 
              onClick={handleBulkStatusUpdate} 
              disabled={bulkProcessing || !bulkNewStatus}
              className="bg-brand hover:bg-brand-hover"
              data-testid="bulk-status-confirm-btn"
            >
              {bulkProcessing ? t('bulk.processing') : t('common.update')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
